/**
 * `expandSectionInstances` — the one function that turns the closed
 * SECTION_TABLE catalogue into the ORDERED list of section instances a
 * specific renewal actually shows (ADR-20261015-h2s6, TG-6a prerequisite
 * group, inserted before TG-6 per that ADR).
 *
 * H2 EXPOSURE-QUESTIONS.md's "Which sections appear" rule: four
 * always-present sections, plus one per renewing line of business,
 * repeated once per policy when two policies share a line (each with its
 * own answers and a policy number shown only because two sections then
 * share a title), plus a runtime-inserted additional-details page after
 * any section whose repeat-block answer selected one — and those pages
 * count as sections in "Section N of M".
 *
 * Reads ONLY through RenewalSummaryPort's own response shape
 * (ADR-20261015-bzyr) and ConfirmedAnswers — no stub, no fixture, no
 * second read path. SECTION_TABLE stays the single catalogue
 * (route-table.ts, AC-14): this file adds no section id of its own and no
 * per-section branch — `lineOfBusinessCode` and `additionalDetailsRules`
 * on each row are what let this stay ONE function for every section type
 * (AC-5's "one row, no per-route code" preserved for instance expansion
 * too).
 */
import {
  encodeSectionInstanceId,
  SECTION_TABLE,
  toRenewalData,
} from "../routing/route-table.ts";
import type { ConfirmedAnswers, SectionDescriptor, SectionId, SectionInstanceId, StepId } from "../routing/route-table.ts";
import type { PolicyInScope } from "./renewal-summary-port.ts";

/**
 * One entry in `expandSectionInstances`'s output. `policyNumber` is
 * present only when this section type has MORE than one instance on this
 * renewal (EXPOSURE-QUESTIONS.md: "a policy number is only shown when two
 * sections share a title") — never merely because the underlying policy
 * happens to have a policy number. It is the id-bearing field:
 * `encodeSectionInstanceId` folds it into `instanceId` exactly when it is
 * present, so saved working-copy/ConfirmedAnswers keys for an existing
 * single-policy section are never disturbed by this field changing
 * (ADR-20261027-81ff).
 *
 * `displayPolicyNumber` is the number shown to the customer: since business rule 12 of
 * m1-portal-h2-fidelity (owner decision 2026-11-03, superseding ADR-20261027-81ff) it is present
 * only where two sections share a title, the same instances that carry `policyNumber`, including
 * an additional-details page inserted after one. Never read by
 * `encodeSectionInstanceId`/`decodeSectionInstanceId`.
 *
 * `additionalDetailsPageKey` is present only when this entry IS a
 * runtime-inserted additional-details page, never on the section
 * instance it was inserted after.
 */
export interface SectionInstance {
  readonly instanceId: SectionInstanceId;
  readonly sectionId: SectionId;
  readonly hostStepId: StepId;
  /** 1-based position in the FULL instance list this function returns,
   * including additional-details pages — directly usable as "Section N"
   * in "Section N of M" once the renderer (TG-6) exists; `M` is simply
   * the returned array's length (AC-15). */
  readonly order: number;
  readonly labelKey: string;
  readonly policyNumber?: string;
  readonly displayPolicyNumber?: string;
  readonly additionalDetailsPageKey?: string;
}

/** One item a repeat-block field's saved answer carries — see
 * `AdditionalDetailsRule`'s own doc comment in route-table.ts for why
 * this shape is this feature's own placeholder, pending the real
 * repeat-block capture UI. */
interface RepeatBlockItem {
  readonly key: string;
  readonly outcome: string;
}

function isRepeatBlockItem(value: unknown): value is RepeatBlockItem {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { key?: unknown }).key === "string" &&
    typeof (value as { outcome?: unknown }).outcome === "string"
  );
}

/** Reads the repeat-block items a section instance's OWN saved answer
 * carries for `fieldId` — `[]` for an unconfirmed instance (no entry in
 * `answers` yet), a malformed value, or a field the instance's answer
 * does not carry at all. Never throws on an opaque/placeholder shape:
 * this is read-only inspection of customer-entered data this task group
 * does not own the rendering of. */
function readRepeatBlockItems(answers: ConfirmedAnswers, instanceId: SectionInstanceId, fieldId: string): readonly RepeatBlockItem[] {
  const savedAnswer = answers[instanceId];
  if (typeof savedAnswer !== "object" || savedAnswer === null) {
    return [];
  }
  const record = savedAnswer as Record<string, unknown>;
  // H2 `buildPages`: rows left behind after the parent question is switched back to "No" leave
  // no pages in the flow. The yes/no answer is stored beside the rows (exposure-answers.ts).
  const parentAnswer = record[`${fieldId}__answer`];
  if (typeof parentAnswer === "string" && parentAnswer !== "yes") {
    return [];
  }
  const raw = record[fieldId];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(isRepeatBlockItem);
}

/**
 * The policies on this renewal whose `sectionIds` includes `code` (a
 * SECTION_TABLE section id — `expandOneSection` passes
 * `section.lineOfBusinessCode`, itself the section's own `id`, see
 * `routing/route-table.ts`'s `perLine`), in the order
 * `renewalSummary.policiesInScope` declared them — the fan-out source for
 * a per-line section's repeated instances.
 *
 * m1-exposure-detail-sourcing TG-4 (ADR-20261020-bppm): consults
 * `policy.sectionIds` (summary 2.1.0, server-computed from the published
 * section-line mapping artefact), not `lineCodes` — the raw Epic line
 * codes `lineCodes` carries were never matchable against this feature's
 * own per-line slugs in the first place (route-table.ts's prior
 * `whenLineOfBusinessRenews` doc comment named that mechanism a
 * placeholder). A policy with no `sectionIds` at all (an instance
 * predating the mapping, or matching no mapping entry) contributes no
 * per-line section — `?? []` never throws on it.
 */
function policiesForLine(policiesInScope: readonly PolicyInScope[], code: string): readonly PolicyInScope[] {
  return policiesInScope.filter((policy) => (policy.sectionIds ?? []).includes(code));
}

/** Builds the single base instance for one matching policy (or the one
 * unrepeated instance, when `policy` is `undefined`) — S3776 helper for
 * `expandOneSection`, factored out so that function's two loops do not
 * nest a third and fourth level of branching inside them. */
function buildSectionInstanceForPolicy(
  section: SectionDescriptor,
  policy: PolicyInScope | undefined,
  showPolicyNumber: boolean,
): {
  readonly instance: Omit<SectionInstance, "order">;
  readonly instanceId: SectionInstanceId;
  readonly policyNumber: string | undefined;
  readonly displayPolicyNumber: string | undefined;
} {
  const policyNumber = showPolicyNumber ? policy?.policyNumber : undefined;
  // m1-portal-h2-fidelity business rule 12 (owner decision 2026-11-03, superseding
  // ADR-20261027-81ff): a policy number is shown only where two sections share a title, so the
  // display number is exactly the id-bearing one.
  const displayPolicyNumber = policyNumber;
  const instanceId = encodeSectionInstanceId({
    sectionId: section.id,
    ...(policyNumber !== undefined ? { policyNumber } : {}),
  });
  const instance: Omit<SectionInstance, "order"> = {
    instanceId,
    sectionId: section.id,
    hostStepId: section.hostStepId,
    labelKey: section.labelKey,
    ...(policyNumber !== undefined ? { policyNumber } : {}),
    ...(displayPolicyNumber !== undefined ? { displayPolicyNumber } : {}),
  };
  return { instance, instanceId, policyNumber, displayPolicyNumber };
}

/** Builds the additional-details pages inserted directly after ONE section
 * instance, per EXPOSURE-QUESTIONS.md's trigger-outcome rule — S3776
 * helper for `expandOneSection`. */
function expandAdditionalDetailsPages(
  section: SectionDescriptor,
  instanceId: SectionInstanceId,
  policyNumber: string | undefined,
  displayPolicyNumber: string | undefined,
  answers: ConfirmedAnswers,
): readonly Omit<SectionInstance, "order">[] {
  const pages: Omit<SectionInstance, "order">[] = [];
  for (const rule of section.additionalDetailsRules ?? []) {
    const items = readRepeatBlockItems(answers, instanceId, rule.fieldId);
    for (const item of items) {
      if (!rule.triggerOutcomes.includes(item.outcome)) {
        continue;
      }
      pages.push({
        instanceId: encodeSectionInstanceId({
          sectionId: section.id,
          ...(policyNumber !== undefined ? { policyNumber } : {}),
          additionalDetailsPageKey: item.key,
        }),
        sectionId: section.id,
        hostStepId: section.hostStepId,
        labelKey: `${section.labelKey}.additionalDetails`,
        ...(policyNumber !== undefined ? { policyNumber } : {}),
        // ADR-20261027-81ff: an additional-details page inherits its
        // owning instance's display policy number -- it belongs to the
        // same policy, even though it never carries the id-bearing field
        // unless its owning instance did.
        ...(displayPolicyNumber !== undefined ? { displayPolicyNumber } : {}),
        additionalDetailsPageKey: item.key,
      });
    }
  }
  return pages;
}

/** Builds the (unordered-within-the-caller, but output in section-table
 * order) instance list for ONE applicable section descriptor: either a
 * single base instance (no `lineOfBusinessCode`) or one instance per
 * matching policy, plus any additional-details pages those instances'
 * own saved answers select — inserted directly after the instance that
 * selected them, per EXPOSURE-QUESTIONS.md. */
function expandOneSection(
  section: SectionDescriptor,
  policiesInScope: readonly PolicyInScope[],
  answers: ConfirmedAnswers,
): readonly Omit<SectionInstance, "order">[] {
  const matchingPolicies =
    section.lineOfBusinessCode === undefined ? [undefined] : policiesForLine(policiesInScope, section.lineOfBusinessCode);
  // EXPOSURE-QUESTIONS.md: a policy number is shown only when two
  // sections share a title -- i.e. only when THIS section type actually
  // repeats on this renewal. A single matching policy (the overwhelmingly
  // common case) renders with the same bare instance id a non-repeated
  // section always had, so every existing ConfirmedAnswers/working-copy
  // entry for it keeps resolving unchanged.
  const showPolicyNumber = matchingPolicies.length > 1;

  const result: Omit<SectionInstance, "order">[] = [];
  for (const policy of matchingPolicies) {
    const { instance, instanceId, policyNumber, displayPolicyNumber } = buildSectionInstanceForPolicy(
      section,
      policy,
      showPolicyNumber,
    );
    result.push(instance, ...expandAdditionalDetailsPages(section, instanceId, policyNumber, displayPolicyNumber, answers));
  }
  return result;
}

/**
 * Returns the ordered instance list for the exposures step's accordion:
 * every applicable `SECTION_TABLE` entry hosted on "exposures"
 * (`section.isApplicable` is the single source of truth for whether a
 * section type appears at all — never recomputed here), expanded to one
 * instance per matching policy, with any selected additional-details
 * pages inserted immediately after the instance that selected them
 * (AC-14, AC-15).
 *
 * Pure and read-only: no I/O, no mutation of `answers`. `renewalSummary`
 * accepts the `policiesInScope` field of RenewalSummaryPort's own
 * `readSummary` response shape (ADR-20261015-bzyr) — this function never
 * imports a stub or fixture. Typed as a minimal structural shape, not the
 * full `RenewalSummaryReadResult`, only so a caller (including this
 * file's own unit tests) can supply a `readonly` array without a cast;
 * the real port's `RenewalSummaryReadResult` satisfies it unchanged.
 */
export function expandSectionInstances(
  renewalSummary: { readonly policiesInScope: readonly PolicyInScope[] },
  answers: ConfirmedAnswers,
): readonly SectionInstance[] {
  const renewalData = toRenewalData(renewalSummary.policiesInScope);
  const exposureSections = SECTION_TABLE.filter((section) => section.hostStepId === "exposures").sort(
    (a, b) => a.order - b.order,
  );

  const withoutOrder: Omit<SectionInstance, "order">[] = [];
  for (const section of exposureSections) {
    if (!section.isApplicable(renewalData, answers)) {
      continue;
    }
    withoutOrder.push(...expandOneSection(section, renewalSummary.policiesInScope, answers));
  }

  return withoutOrder.map((instance, index) => ({ ...instance, order: index + 1 }));
}
